import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Lock, AlertTriangle, Mail, Send, Phone, MessageCircle, Hash } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { resolveAsset } from "@/components/kit/FileUpload";
import { TwoFactorChallenge } from "@/components/auth/TwoFactorChallenge";
import { RecoveryFlow } from "@/components/auth/RecoveryFlow";
import { useForceDark } from "@/context/ThemeContext";

function Shell({ ctx, bgKey, children, testId, heading, sub }) {
  const b = ctx?.branding || {};
  const side = resolveAsset(b.side_image_url || b[bgKey] || b.login_bg_url || b.registration_bg_url);
  const logo = resolveAsset(b.logo_url);
  const name = ctx?.network_name || "Affiliate Portal";
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4 py-8" data-testid={testId}>
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0E0E11] shadow-2xl">
        <div className="flex h-14 items-center justify-center border-b border-white/[0.06] px-6">
          {logo ? <img src={logo} alt="" className="h-7 w-auto object-contain" data-testid="portal-logo" />
            : <span className="text-base font-semibold tracking-wide text-white" data-testid="portal-network-name">{name}</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative hidden min-h-[420px] md:block">
            {side
              ? <img src={side} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="portal-side-image" />
              : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[#141417] via-[#1a1a1e] to-[#0A0A0A] p-8 text-center">
                  <div className="text-3xl font-bold text-white/90">{name}</div>
                  <div className="h-px w-20 bg-white/20" />
                  <p className="text-sm text-white/40">Partner network</p>
                </div>
              )}
          </div>
          <div className="p-8">
            {(heading || !logo) && <h1 className="mb-1 text-2xl font-semibold text-white">{heading || name}</h1>}
            {sub && <p className="mb-5 text-sm text-white/55">{sub}</p>}
            {children}
            {b.footer_text && <p className="mt-6 text-center text-xs text-white/40">{b.footer_text}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ icon: Icon, color, title, text, testId }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4" data-testid={testId}>
      <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#141417] p-8 text-center">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: `${color}22` }}>
          <Icon className="h-6 w-6" style={{ color }} />
        </span>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <p className="mt-2 text-sm text-white/60">{text}</p>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-white/30";

function LoginView({ ctx, slug, base }) {
  const { portalLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [held, setHeld] = useState(null);
  const accent = ctx.branding?.accent_color || "#22C55E";
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const r = await portalLogin(email, password, slug, remember);
    setBusy(false);
    if (r.ok) return;
    if (r.twofa) setChallenge(r.challenge);
    else if (r.hold) setHeld(r.result);
    else setErr(r.error || "Invalid email or password");
  };
  if (held) return <PendingView result={held} accent={accent} base={base} onBack={() => setHeld(null)} />;
  return (
    <Shell ctx={ctx} bgKey="login_bg_url" testId="portal-login" heading="Sign in" sub="Log in to your partner account">
      {challenge ? (
        <TwoFactorChallenge challenge={challenge} accent={accent} accentText="#000000" remember={remember} onCancel={() => { setChallenge(null); setPassword(""); }} />
      ) : (
      <>
      <form onSubmit={submit} className="space-y-4" data-testid="portal-login-form">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white/70">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@example.com" data-testid="portal-login-email" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white/70">Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="Password" data-testid="portal-login-password" />
        </div>
        {err && <p className="text-sm text-[#EF4444]" data-testid="portal-login-error">{err}</p>}
        <label className="flex cursor-pointer items-center gap-2 text-xs text-white/60" data-testid="portal-remember-label">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            data-testid="portal-login-remember"
            className="h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border border-white/30 bg-transparent transition-colors"
            style={remember ? { background: accent, borderColor: accent } : undefined} />
          Trust this device for 30 days
        </label>
        <Button type="submit" disabled={busy} data-testid="portal-login-submit" className="w-full text-black hover:opacity-90" style={{ background: accent }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs">
        <Link to={`${base}/forgot-password`} className="text-white/50 hover:text-white" data-testid="portal-forgot-link">Forgot password?</Link>
        <Link to={`${base}/register`} className="font-medium" style={{ color: accent }} data-testid="portal-create-account">Create account</Link>
      </div>
      </>
      )}
    </Shell>
  );
}

const CONTACT_META = {
  email: { icon: Mail, label: "Email", href: (v) => `mailto:${v}` },
  telegram: { icon: Send, label: "Telegram", href: (v) => `https://t.me/${String(v).replace(/^@/, "")}` },
  discord: { icon: Hash, label: "Discord", href: null },
  phone: { icon: Phone, label: "Phone", href: (v) => `tel:${v}` },
};

export function PendingView({ result, accent = "#22C55E", base = "", onBack }) {
  const r = result?.responsible;
  const msg = result?.message || "Thanks for registering. Your application has been submitted and is awaiting approval.";
  const avatar = r ? resolveAsset(r.avatar) : null;
  const contacts = r ? Object.entries(r.contacts || {}) : [];
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4 py-8" data-testid="portal-register-pending">
      <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#141417] p-8 text-center">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: `${accent}22` }}>
          <CheckCircle2 className="h-6 w-6" style={{ color: accent }} />
        </span>
        <h2 className="text-lg font-semibold text-white">Application submitted</h2>
        <p className="mt-2 text-sm text-white/60">{msg}</p>

        {r && (
          <div className="mt-6 rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-5 text-left" data-testid="portal-responsible-card">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-white/40">Your account manager</p>
            <div className="flex items-center gap-3">
              {avatar
                ? <img src={avatar} alt="" className="h-12 w-12 rounded-full object-cover" data-testid="portal-responsible-avatar" />
                : <span className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold text-black" style={{ background: accent }}>{(r.name || "?").slice(0, 1).toUpperCase()}</span>}
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white" data-testid="portal-responsible-name">{r.name}</div>
                <div className="text-xs text-white/50">{r.title || "Account Manager"}</div>
              </div>
            </div>
            {contacts.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="portal-responsible-contacts">
                {contacts.map(([key, val]) => {
                  const meta = CONTACT_META[key] || { icon: MessageCircle, label: key, href: null };
                  const Icon = meta.icon;
                  const inner = (
                    <>
                      <Icon className="h-4 w-4 shrink-0 text-white/40" />
                      <span className="truncate text-white/80">{val}</span>
                    </>
                  );
                  return meta.href ? (
                    <a key={key} href={meta.href(val)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-sm transition-colors hover:border-white/20"
                      data-testid={`portal-contact-${key}`}>{inner}</a>
                  ) : (
                    <div key={key} className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-sm" data-testid={`portal-contact-${key}`}>{inner}</div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {onBack ? (
          <Button onClick={onBack} data-testid="portal-back-to-login" className="mt-6 w-full text-black hover:opacity-90" style={{ background: accent }}>Back to Login</Button>
        ) : (
          <Link to={`${base}/login`} data-testid="portal-back-to-login">
            <Button className="mt-6 w-full text-black hover:opacity-90" style={{ background: accent }}>Back to Login</Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function RegisterView({ ctx, slug, base }) {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const accent = ctx.branding?.accent_color || "#22C55E";
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  if (!ctx.open) {
    return <Centered icon={Lock} color="#EF4444" title="Registration is currently closed"
      text="This affiliate network is not accepting new sign-ups right now. Please check back later." testId="portal-register-closed" />;
  }
  if (result) {
    return <PendingView result={result} accent={accent} base={base} />;
  }
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const fields = {};
      (ctx.fields || []).forEach((f) => { if (!["email", "password"].includes(f.key)) fields[f.key] = values[f.key] || ""; });
      const { data } = await http.post("/public/register", { email: values.email || "", password: values.password || "", slug: slug || null, fields });
      if (data.auto_login) { await refresh(); navigate("/"); return; }
      setResult(data);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };
  return (
    <Shell ctx={ctx} bgKey="registration_bg_url" testId="portal-register" heading="Create account" sub="Join the affiliate network">
      <form onSubmit={submit} className="space-y-4" data-testid="portal-register-form">
        {(ctx.fields || []).map((f) => (
          <div key={f.key} className="space-y-1.5">
            <label className="text-xs font-medium text-white/70">{f.label}{f.required && <span className="ml-0.5 text-[#EF4444]">*</span>}</label>
            <input type={f.type || "text"} required={f.required} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`portal-field-${f.key}`} className={inputCls} placeholder={f.label} />
          </div>
        ))}
        {err && <p className="text-sm text-[#EF4444]" data-testid="portal-register-error">{err}</p>}
        <Button type="submit" disabled={busy} data-testid="portal-register-submit" className="w-full text-black hover:opacity-90" style={{ background: accent }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <p className="mt-4 text-center text-xs text-white/50">
        Already have an account? <Link to={`${base}/login`} className="font-medium" style={{ color: accent }} data-testid="portal-back-login">Log in</Link>
      </p>
    </Shell>
  );
}

function ForgotView({ ctx, slug, base }) {
  const navigate = useNavigate();
  const accent = ctx.branding?.accent_color || "#22C55E";
  return (
    <Shell ctx={ctx} bgKey="login_bg_url" testId="portal-forgot">
      <RecoveryFlow accent={accent} onBack={() => navigate(`${base}/login`)} />
    </Shell>
  );
}

export default function PortalEntry({ page }) {
  useForceDark();
  const { slug: pathSlug } = useParams();
  const slug = pathSlug || null;
  const base = slug ? `/portal/${slug}` : "";
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);

  // Real favicon + tab title for the partner portal: Favicon field -> <link rel="icon">,
  // Logo stays a visual logo only (rendered at the top of the Shell).
  useEffect(() => {
    if (!ctx?.found) return;
    if (ctx.network_name) document.title = ctx.network_name;
    const fav = resolveAsset(ctx.branding?.favicon_url);
    if (!fav) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = fav;
  }, [ctx]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get("/public/portal-context", { params: slug ? { slug } : {} });
        if (alive) setCtx(data);
      } catch { if (alive) setCtx({ found: false }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>;
  if (!ctx?.found) return <Centered icon={AlertTriangle} color="#EAB308" title="Affiliate network not found" text="This portal link is invalid or no longer available." testId="portal-notfound" />;

  if (page === "register") return <RegisterView ctx={ctx} slug={slug} base={base} />;
  if (page === "forgot") return <ForgotView ctx={ctx} slug={slug} base={base} />;
  return <LoginView ctx={ctx} slug={slug} base={base} />;
}
